import { useEffect, useLayoutEffect, useRef, useSyncExternalStore } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';
import { queryClient } from '@/providers/query-provider';
import { noteOpen, warmFavourites } from '@/lib/route-preload';

/**
 * What the reader is waiting for, from the query cache, for the whole shell:
 *   1 - a page's first answer is loading (nothing of it to show yet);
 *   2 - a page is showing its PREVIOUS answer while the new one loads (a new
 *       window, a new filter: REPORT_QUERY's placeholderData).
 * Only queries a mounted page observes count. A background refetch of an
 * answer that is still for the same question, a poll, or a prefetch nobody
 * is reading are not waits and draw nothing.
 */
function readActivity() {
  let v = 0;
  for (const q of queryClient.getQueryCache().getAll()) {
    if (q.state.fetchStatus !== 'fetching' || !q.observers.length) continue;
    if (q.state.status === 'pending') v |= 1;
    // meta.dimPage: false - a card re-asking its own question (a budget typed
    // into the plan) shows the line but does not grey out the whole page.
    if (q.observers.some((o) => o.getCurrentResult().isPlaceholderData)) v |= q.meta?.dimPage === false ? 1 : 2;
    if (v === 3) break;
  }
  return v;
}
const subscribe = (cb) => queryClient.getQueryCache().subscribe(cb);

export function useReportActivity() {
  const v = useSyncExternalStore(subscribe, readActivity, readActivity);
  return { loading: !!(v & 1), stale: !!(v & 2) };
}

/**
 * The thin line along the top of the content panel, and the dim on the page
 * under it. Its own component so a change re-renders this line and nothing
 * else; the dim is an attribute on <main> that App.css turns into opacity on
 * the page (never on this line). The line is sticky and zero-height in the
 * flow, fades in only after 150ms so an answer from cache never flickers it,
 * and moves by transform alone.
 */
export function ReportActivity({ mainRef }) {
  const { loading, stale } = useReportActivity();
  useLayoutEffect(() => {
    const el = mainRef.current;
    if (!el) return;
    if (stale) el.setAttribute('data-report-stale', 'true'); else el.removeAttribute('data-report-stale');
    if (loading || stale) el.setAttribute('aria-busy', 'true'); else el.removeAttribute('aria-busy');
  }, [mainRef, loading, stale]);
  return <div className="report-progress" data-on={loading || stale ? 'true' : 'false'} aria-hidden />;
}

/**
 * Per-navigation behaviour of the content panel, which is the app's scroller
 * (<main>, not the window, so the browser's own restoration never applies):
 *   - a new page opens at its top;
 *   - Back and Forward return to where the reader was on that page, waiting
 *     for the content to be tall enough rather than landing short;
 *   - a change of window, tab or filter (same path) leaves the position alone.
 * Also counts which reports this person opens, and once the first page has
 * finished loading, warms the ones they open most in idle time.
 */
const positions = new Map();
export function RouteFeel({ mainRef }) {
  const location = useLocation();
  const navType = useNavigationType();
  const lastPath = useRef(null);

  // Remember the position of the entry being read, continuously. One listener
  // reading the key from a ref: the key moves on in the same commit that
  // swaps the page, so the scroll the swap itself causes (the browser
  // clamping to a shorter page) is filed under the NEW entry, never over the
  // position being left.
  const keyRef = useRef(location.key);
  useEffect(() => {
    const el = mainRef.current;
    if (!el) return undefined;
    let raf = 0;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => { raf = 0; positions.set(keyRef.current, el.scrollTop); });
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => { el.removeEventListener('scroll', onScroll); if (raf) cancelAnimationFrame(raf); };
  }, [mainRef]);

  useLayoutEffect(() => {
    keyRef.current = location.key;
    const el = mainRef.current;
    const pathChanged = lastPath.current !== null && lastPath.current !== location.pathname;
    lastPath.current = location.pathname;
    if (!el) return undefined;
    if (navType === 'POP' && positions.has(location.key)) {
      const want = positions.get(location.key);
      // The page may still be drawing its rows: keep asking for the position
      // until the panel is tall enough, for up to two seconds, and stop the
      // moment the reader scrolls themselves.
      let raf = 0; let stopped = false;
      const stop = () => { stopped = true; };
      el.addEventListener('wheel', stop, { passive: true, once: true });
      el.addEventListener('touchstart', stop, { passive: true, once: true });
      const until = performance.now() + 2000;
      const tick = () => {
        if (stopped) return;
        el.scrollTop = want;
        if (Math.abs(el.scrollTop - want) > 2 && performance.now() < until) raf = requestAnimationFrame(tick);
      };
      tick();
      return () => { stopped = true; cancelAnimationFrame(raf); el.removeEventListener('wheel', stop); el.removeEventListener('touchstart', stop); };
    }
    if (pathChanged && navType !== 'REPLACE') el.scrollTop = 0;
    return undefined;
  }, [location.key, location.pathname, navType, mainRef]);

  useEffect(() => { noteOpen(location.pathname); }, [location.pathname]);

  // Once per page load: after the first page's own requests have finished,
  // warm the reports this person opens most, when the browser is idle.
  const warmedOnce = useRef(false);
  useEffect(() => {
    if (warmedOnce.current) return undefined;
    let idle = 0; let timer = 0;
    const tryWarm = () => {
      if (warmedOnce.current) return;
      if (queryClient.isFetching() > 0) { timer = setTimeout(tryWarm, 700); return; }
      warmedOnce.current = true;
      const run = () => warmFavourites(window.location.pathname.replace(/^\/app/, '') || '/');
      if (window.requestIdleCallback) idle = window.requestIdleCallback(run, { timeout: 3000 });
      else timer = setTimeout(run, 300);
    };
    timer = setTimeout(tryWarm, 1200);
    return () => { clearTimeout(timer); if (idle && window.cancelIdleCallback) window.cancelIdleCallback(idle); };
    // Only the first page decides when; later navigations do not re-arm it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
}
